import { useState } from 'react';
import { Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { formatCurrency } from '@/lib/utils';
import { PdfViewerDialog } from '@/components/shared/PdfViewerDialog';
import { PaymentHistoryList } from './PaymentHistoryList';
import { SendHistoryList } from './SendHistoryList';
import { MarkPaidDialog } from './MarkPaidDialog';

/**
 * One invoice's ledger — what was paid, and the way to settle or reverse it.
 * Shared, because the workspace and the job Billing tab both open it and it had
 * begun to exist twice.
 *
 * EXACTLY ONE dialog is open at a time. Both surfaces previously rendered the
 * history dialog and the record dialog as open siblings, which does not work:
 * two modal Radix dialogs each mark the other's portal aria-hidden, so the
 * record form is invisible to assistive tech and to Playwright, and in practice
 * the user just sees the history behind it. Recording is a step in the panel,
 * not a layer on top of it.
 *
 * That also makes "Record payment" from a row menu go straight to the form,
 * which is what choosing it means.
 *
 * The form itself takes a part payment (NEW-237), so this panel stays open to
 * recording while the invoice is `partially_paid` — which it already did, for
 * the legacy rows that could reach that state before the UI could create one.
 *
 * `'sends'` is the email log (Task 19). The panel is a modal dialog opened per
 * invoice — nothing sits "beside" it on the job Billing tab — so reusing this
 * same mode switch, rather than a second dialog, is what keeps the two-modal
 * rule above satisfied by construction.
 *
 * `'sentPdf'` is NEW-238's archived copy of one emailed attempt, and is a mode
 * for exactly that reason: the send log it opens from already lives inside this
 * dialog, so a viewer on top of it would be the second modal.
 */

type Mode = 'history' | 'record' | 'sends' | 'sentPdf';

export interface PaymentsPanelInvoice {
  invoiceId: string;
  jobId: string;
  invoiceNumber: string;
  amount: number;
  collected: number;
  outstanding: number;
  lifecycle: string;
  /** A backfilled row whose amount was never recorded. */
  amountUnknown?: boolean;
}

export interface PaymentsPanelProps {
  /** The invoice the panel is open for; null closes it. */
  invoice: PaymentsPanelInvoice | null;
  onClose: () => void;
  /** Open straight into the mark-as-paid form rather than the history. */
  startRecording?: boolean;
  /** Fired after a ledger write, for callers with caches the hooks don't know. */
  onRecorded?: () => void;
}

export function PaymentsPanel({
  invoice,
  onClose,
  startRecording = false,
  onRecorded,
}: PaymentsPanelProps) {
  // Mount-time only. Callers key this component by invoice id so each open
  // starts from the mode they asked for.
  const [mode, setMode] = useState<Mode>(startRecording ? 'record' : 'history');
  // The send whose archived copy is on screen, and the date it was sent on.
  // Held beside `mode` rather than inside it so returning to the log does not
  // have to reconstruct them.
  //
  // The date comes from the row that was clicked rather than from the response:
  // it names the file, and Content-Disposition is unreadable cross-origin.
  const [sentPdf, setSentPdf] = useState<{ sendId: string; sentOn: string } | null>(null);

  if (!invoice) return null;

  // Nothing left to collect, and a voided invoice is not a debt.
  const canRecord = invoice.lifecycle !== 'voided' && invoice.lifecycle !== 'paid';

  if (mode === 'record') {
    return (
      <MarkPaidDialog
        open
        onOpenChange={(open) => {
          if (open) return;
          // Coming from the history, go back to it; opened straight into the
          // form, closing the form closes the panel.
          if (startRecording) onClose();
          else setMode('history');
        }}
        invoiceId={invoice.invoiceId}
        jobId={invoice.jobId}
        invoiceNumber={invoice.invoiceNumber}
        outstanding={invoice.outstanding}
        amountUnknown={invoice.amountUnknown}
        onRecorded={onRecorded}
      />
    );
  }

  // NEW-238's archived copy, as a MODE rather than a second dialog — the same
  // reason 'sends' is one. Closing it returns to the log it was opened from.
  if (mode === 'sentPdf' && sentPdf) {
    // ONE name for the window and the download, and the one the server put in
    // Content-Disposition: `<invoiceNumber>-sent-<YYYY-MM-DD>.pdf`. Dated,
    // because two sends of one invoice would otherwise save over each other;
    // rebuilt here, because the browser cannot read the server's name
    // cross-origin; and filename-shaped, because FileViewerShell's title IS
    // the filename and the viewer's accessible name.
    const sentPdfName = `${invoice.invoiceNumber}-sent-${sentPdf.sentOn}.pdf`;
    return (
      <PdfViewerDialog
        path={`/invoices/${invoice.invoiceId}/sends/${sentPdf.sendId}/pdf`}
        title={sentPdfName}
        downloadName={sentPdfName}
        onClose={() => {
          setSentPdf(null);
          setMode('sends');
        }}
      />
    );
  }

  const isSends = mode === 'sends';

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {isSends ? 'Emails' : 'Payments'} — {invoice.invoiceNumber}
          </DialogTitle>
          <DialogDescription>
            {isSends
              ? 'Every email attempt for this invoice, newest first.'
              : `${formatCurrency(invoice.collected)} collected of ${formatCurrency(invoice.amount)} — ${formatCurrency(invoice.outstanding)} outstanding.`}
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-1 border-b border-gray-100 pb-2">
          <Button
            type="button"
            variant={isSends ? 'ghost' : 'secondary'}
            size="sm"
            onClick={() => setMode('history')}
          >
            Payments
          </Button>
          <Button
            type="button"
            variant={isSends ? 'secondary' : 'ghost'}
            size="sm"
            onClick={() => setMode('sends')}
          >
            Emails
          </Button>
        </div>

        {isSends ? (
          <SendHistoryList
            invoiceId={invoice.invoiceId}
            onViewSentPdf={(sendId, sentOn) => {
              setSentPdf({ sendId, sentOn });
              setMode('sentPdf');
            }}
          />
        ) : (
          <PaymentHistoryList
            invoiceId={invoice.invoiceId}
            jobId={invoice.jobId}
            // Caps an amend at this invoice's remaining balance plus whatever
            // the amended row itself holds — see PaymentHistoryList.
            outstanding={invoice.outstanding}
          />
        )}

        {!isSends && canRecord && (
          <Button onClick={() => setMode('record')}>
            <Check className="mr-2 h-4 w-4" />
            Record payment
          </Button>
        )}
      </DialogContent>
    </Dialog>
  );
}
